<template>
  <div class="flex justify-end p-2 m-0 mt-6">
    <!-- Save and Create Next button -->
    <Button
      v-if="editMode && isAnyFieldDirty && onSaveAndCreateNext"
      :icon="saveAndCreateNextButtonIcon"
      :label="computedSaveAndCreateNextLabel"
      @click="onSaveAndCreateNext"
    />

    <ButtonGroup class="bg-primary ml-auto">
      <!-- Reset button -->
      <Button
        v-if="onConfirmResetChanges !== undefined && !isNew && editMode && isAnyFieldDirty"
        :icon="resetButtonIcon"
        :label="computedResetLabel"
        outlined
        :disabled="!profileStore.userProfile?.isSeyboldUser"
        @click="confirmResetChanges"
      />

      <!-- Save button -->
      <Button
        v-if="editMode && isAnyFieldDirty"
        :icon="saveButtonIcon"
        :label="computedSaveLabel"
        :disabled="!profileStore.userProfile?.isSeyboldUser"
        @click="onSubmit"
      />
    </ButtonGroup>
  </div>
  <Confirm ref="confirmDialog" />
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Confirm from '@/components/confirm/Confirm.vue';
import { useProfileStore } from '@/stores/profile';

const { t } = useI18n();
const profileStore = useProfileStore();

const confirmDialog = ref<InstanceType<typeof Confirm> | null>(null);

const props = withDefaults(
  defineProps<{
    isNew: boolean;
    editMode: boolean;
    isAnyFieldDirty: boolean;
    onGoToList: () => void;
    onToggleEditMode: () => void;
    onSubmit: () => void;
    onConfirmResetChanges?: () => void;
    onSaveAndCreateNext?: () => void;

    /** Customization (icons & labels) */
    saveButtonIcon?: string;
    saveButtonLabel?: string;
    resetButtonIcon?: string;
    resetButtonLabel?: string;
    saveAndCreateNextButtonIcon?: string;
    saveAndCreateNextButtonLabel?: string;
  }>(),
  {
    saveButtonIcon: 'pi pi-check',
    resetButtonIcon: 'pi pi-refresh',
    saveAndCreateNextButtonIcon: 'pi pi-check',
    onConfirmResetChanges: () => undefined,
    onSaveAndCreateNext: undefined,
    saveButtonLabel: undefined,
    resetButtonLabel: undefined,
    saveAndCreateNextButtonLabel: undefined,
  },
);

/** Compute labels with sensible fallbacks */
// Keep original behavior; only apply custom labels if provided
const computedSaveLabel = computed(() => props.saveButtonLabel ?? t('buttons.save'));
const computedResetLabel = computed(() => props.resetButtonLabel ?? undefined);
const computedSaveAndCreateNextLabel = computed(
  () => props.saveAndCreateNextButtonLabel ?? t('buttons.saveAndCreateNext'),
);

async function confirmResetChanges() {
  if (!confirmDialog.value) return;

  const isConfirmed = await confirmDialog.value.isResetConfirmed();

  if (isConfirmed && props.onConfirmResetChanges) {
    props.onConfirmResetChanges();
  }
}
</script>
